// @systeme: emargement
// ============================================================================
// PairsForm — Page publique d'ÉMARGEMENT par signature
// ============================================================================
// Accessible via le lien individuel de l'email d'émargement (sans auth).
// Route : /emarger/:token. DISTINCTE de la confirmation de présence : ici on
// capture une SIGNATURE manuscrite (`SignaturePad`, canvas → PNG) qui, avec
// l'IP + l'horodatage enregistrés côté serveur, rend la présence opposable
// (statut PRESENT). Lot 7.6 : le pad n'est plus recopié ici — c'est le même
// composant que l'émargement direct in-app (page Séance).
// ============================================================================

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Card, CardContent, Typography, Box, CircularProgress } from "@mui/material";
import { CheckCircle, Calendar, Clock, AlertTriangle } from "lucide-react";
import { api, statutApi } from "../lib/api";
import { formatDateJourFR } from "@cecodev/shared";
import SignaturePad from "../components/SignaturePad";

interface SeanceInfo {
  dateSeance: string;
  heureDebut: string;
  heureFin: string;
  numero?: number;
  groupeNom: string;
  /** null en Inter : le projet hôte ne se nomme pas (lot 8.2). */
  projetNom: string | null;
}

export default function EmargementPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<"loading" | "ok" | "done" | "expired" | "error">("loading");
  const [seance, setSeance] = useState<SeanceInfo | null>(null);
  const [signataireNom, setSignataireNom] = useState("");
  const [signataireRole, setSignataireRole] = useState<"PARTICIPANT" | "ANIMATEUR" | "COMMUNAUTE">("PARTICIPANT");
  const [dejaSigne, setDejaSigne] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Charge le contexte (séance, signataire, déjà signé ?).
  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    api
      .get(`/emargement/${token}`)
      .then((res) => {
        setSeance(res.data.seance);
        setSignataireNom(res.data.signataireNom || "");
        setSignataireRole(res.data.signataireRole || "PARTICIPANT");
        setDejaSigne(!!res.data.dejaSigne);
        setStatus(res.data.dejaSigne ? "done" : "ok");
      })
      .catch((err: unknown) => setStatus(statutApi(err) === 410 ? "expired" : "error"));
  }, [token]);

  async function signer(signatureData: string) {
    if (!token) return;
    setSubmitting(true);
    try {
      await api.post(`/emargement/${token}`, { signatureData });
      setStatus("done");
      setDejaSigne(false);
    } catch (err) {
      if (statutApi(err) === 410) setStatus("expired");
      // sinon on laisse l'utilisateur réessayer
    } finally {
      setSubmitting(false);
    }
  }

  if (status === "loading") {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }} color="text.secondary">Chargement…</Typography>
      </Container>
    );
  }

  const dateFormatted = seance?.dateSeance
    ? formatDateJourFR(seance.dateSeance)
    : "";

  const SeanceBox = seance ? (
    <Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2, mb: 3, textAlign: "left" }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {[seance.projetNom, seance.groupeNom].filter(Boolean).join(" — ")}{seance.numero ? ` · Séance ${seance.numero}` : ""}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mb: 0.5 }}>
        <Calendar size={16} /> <Typography variant="body2">{dateFormatted}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
        <Clock size={16} /> <Typography variant="body2">{seance.heureDebut} — {seance.heureFin}</Typography>
      </Box>
    </Box>
  ) : null;

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: "#1E4A43", textAlign: "center" }}>
            PairsForm
          </Typography>

          {status === "expired" && (
            <Box sx={{ textAlign: "center" }}>
              <AlertTriangle size={56} color="#f59e0b" />
              <Typography variant="h6" sx={{ mt: 2 }}>Lien expiré</Typography>
              <Typography color="text.secondary">Ce lien d'émargement n'est plus valable. Demandez à votre animateur de vous le renvoyer.</Typography>
            </Box>
          )}

          {status === "error" && (
            <Box sx={{ textAlign: "center" }}>
              <AlertTriangle size={56} color="#dc2626" />
              <Typography variant="h6" sx={{ mt: 2 }}>Lien invalide</Typography>
              <Typography color="text.secondary">Ce lien d'émargement est invalide.</Typography>
            </Box>
          )}

          {status === "done" && (
            <Box sx={{ textAlign: "center" }}>
              <CheckCircle size={64} color="#4BB7A5" />
              <Typography variant="h5" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                {dejaSigne ? "Déjà émargé" : "Présence émargée !"}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Merci{signataireNom ? ` ${signataireNom}` : ""}, votre présence à cette séance est enregistrée et horodatée.
              </Typography>
              {SeanceBox}
            </Box>
          )}

          {status === "ok" && (
            <>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, textAlign: "center" }}>
                Émargez votre présence
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
                {signataireNom ? `${signataireNom} — ` : ""}{signataireRole === "ANIMATEUR" ? "animateur" : signataireRole === "COMMUNAUTE" ? "membre de la communauté" : "participant"}
              </Typography>
              {SeanceBox}
              <Typography variant="body2" sx={{ mb: 1 }}>Signez dans le cadre ci-dessous :</Typography>
              <SignaturePad onSign={signer} submitting={submitting} submitLabel="Signer ma présence" />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                En signant, vous attestez votre présence à cette séance. Votre signature est horodatée et conservée à titre de preuve.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
